import { useState, useEffect } from 'react';
import { djs } from '@duke-hq/djs';
import { Controller, useForm } from 'react-hook-form';
import {
  Button,
  VStack,
  Modal,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalOverlay,
  Textarea,
  useToast,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  IconButton,
  useDisclosure,
  Stack,
  Input,
} from '@chakra-ui/react';
import { Select } from 'chakra-react-select';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import { getAdminNoteCategoryOptions } from '@duke-hq/ui/components/Notes/utils';
import { Ellipsis, CalendarSync, CircleOff, History, ClipboardCheck } from 'lucide-react';
import { StudentCourseEnrolmentStatus, AdminNoteCategory } from '@duke-hq/svc-api-db';
import { useCheckPermission } from '@duke-hq/app-admin-web/hooks/useCheckPermission';
import { StudentResourceEnrolmentStatusSchema } from '@duke-hq/svc-api-db/schemas/enums/StudentResourceEnrolmentStatus.schema';
import {
  RESOURCE_STATUSES,
  type RESOURCE_STATUSES_TYPE,
} from '@duke-hq/shared-constants/resourceStatuses';
import { UnitHistoryTimeline } from './UnitHistoryTimeline';
import { SemesterCompletionAlert } from './SemesterCompletionAlert';
import DialogBox from '@duke-hq/ui/components/DialogBox/DialogBox';

type UnitAdjustmentsProps = {
  enrolmentId: number;
  unitOfCompetencyId: string;
  previousItemDueDate: Date | string | null;
  enrolmentStatus: StudentCourseEnrolmentStatus;
  unitScheduleOverallStatus: RESOURCE_STATUSES_TYPE;
  isAccredited: boolean;
  isLLN: boolean;
  isWorkPlacement: boolean;
  isWorkPlacementComplete: boolean;
};

export function UnitAdjustments({
  enrolmentId,
  unitOfCompetencyId,
  previousItemDueDate,
  enrolmentStatus,
  unitScheduleOverallStatus,
  isAccredited,
  isLLN,
  isWorkPlacement,
  isWorkPlacementComplete,
}: UnitAdjustmentsProps) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isUpdateScheduleOpen,
    onOpen: onUpdateScheduleOpen,
    onClose: onUpdateScheduleClose,
  } = useDisclosure();

  const {
    isOpen: isUnitHistoryOpen,
    onOpen: onUnitHistoryOpen,
    onClose: onUnitHistoryClose,
  } = useDisclosure();

  const { isOpen: isMarkWpOpen, onOpen: onMarkWpOpen, onClose: onMarkWpClose } = useDisclosure();

  const [schedule, setSchedule] = useState<Date | null>(null);
  const [wpNotes, setWpNotes] = useState('');
  const canAssessWorkPlacements = useCheckPermission('canAssessWorkPlacements');
  const [isMarkingNotRequired, setIsMarkingNotRequired] = useState(false);

  const { data } = trpcAdmin.enrolment.getUnitHistory.useQuery(
    {
      courseEnrolmentId: enrolmentId,
      unitOfCompetencyId,
    },
    { enabled: isUnitHistoryOpen },
  );

  const markUnitAsNotRequired = trpcAdmin.enrolment.markUnitAsNotRequired.useMutation({
    onMutate() {
      setIsMarkingNotRequired(true);
    },
    onSuccess() {
      toast({
        status: 'success',
        title: t`Marked unit as not required`,
      });
      setIsMarkingNotRequired(false);
    },
    onError() {
      toast({
        status: 'error',
        title: t`Failed to mark unit as not required`,
      });
      setIsMarkingNotRequired(false);
    },
  });

  const [isUpdatingSchedule, setIsUpdatingSchedule] = useState(false);

  const updateSchedule = trpcAdmin.enrolment.updateSchedule.useMutation({
    onSuccess() {
      toast({
        status: 'success',
        title: t`Study Schedule Updated`,
      });
      void utils.enrolment.byIdWithResourceStatus.refetch();
      setIsUpdatingSchedule(false);
    },
    onError(error) {
      toast({
        status: 'error',
        title: error.message,
      });
      setIsUpdatingSchedule(false);
    },
  });

  const markWpComplete = trpcAdmin.workPlacements.placements.markComplete.useMutation({
    onSuccess(data) {
      if (data.completedUnitCount === 0) {
        toast({
          status: 'info',
          title: t`No work placement units to complete`,
        });
        onMarkWpClose();
        setWpNotes('');
        return;
      }
      toast({
        status: 'success',
        title: t`Work placement marked as complete`,
      });
      onMarkWpClose();
      setWpNotes('');
      void utils.enrolment.byIdWithResourceStatus.invalidate({
        enrolmentId: Number(enrolmentId),
      });
    },
    onError(error) {
      toast({
        status: 'error',
        title: error.message,
      });
    },
  });

  const onScheduleUpdate = async (data: { category?: string; text: string }) => {
    if (schedule) {
      setIsUpdatingSchedule(true);
      await updateSchedule.mutateAsync({
        ...data,
        enrolmentId,
        unitOfCompetencyId,
        startAt: schedule,
      });
      onUpdateScheduleClose();
    } else {
      toast({
        status: 'error',
        title: t`No start date selected`,
      });
    }
  };

  // NOT_REQUIRED is only an option for non-accredited units + LLNs
  const noteTypeOptions = getAdminNoteCategoryOptions('UNIT_NOT_REQUIRED').filter(
    (option) => !isAccredited || isLLN || option.value !== 'NOT_REQUIRED',
  );

  const {
    handleSubmit,
    control,
    clearErrors,
    reset,
    formState: { errors },
  } = useForm<{
    category: AdminNoteCategory;
    text: string;
  }>({
    mode: 'onBlur',
    reValidateMode: 'onSubmit',
  });

  const utils = trpcAdmin.useUtils();
  const toast = useToast();

  const handleClose = () => {
    reset();
    onClose();
  };

  const onFormChange = () => {
    clearErrors();
  };

  const onMarkUnitAsNotRequired = async (data: { category: AdminNoteCategory; text: string }) => {
    await markUnitAsNotRequired.mutateAsync({
      text: data.text,
      enrolmentId,
      unitOfCompetencyId,
      category: data.category,
      unitStatus: StudentResourceEnrolmentStatusSchema.parse(unitScheduleOverallStatus),
    });

    handleClose();
    void utils.enrolment.byIdWithResourceStatus.invalidate({
      enrolmentId: Number(enrolmentId),
    });
  };

  useEffect(() => {
    // Synchronize local schedule state with prop changes
    // eslint-disable-next-line @eslint-react/hooks-extra/no-direct-set-state-in-use-effect
    setSchedule(previousItemDueDate != null ? djs(previousItemDueDate).toDate() : null);
  }, [previousItemDueDate]);

  const isUnitExempted =
    unitScheduleOverallStatus === RESOURCE_STATUSES.CREDIT_TRANSFER ||
    unitScheduleOverallStatus === RESOURCE_STATUSES.RECOGNITION_OF_PRIOR_LEARNING ||
    unitScheduleOverallStatus === RESOURCE_STATUSES.NOT_REQUIRED;

  const { data: wouldCompleteCourseData } =
    trpcAdmin.enrolment.getWouldCompleteCourseOnWorkPlacement.useQuery(
      {
        courseEnrolmentId: enrolmentId,
        unitOfCompetencyIds: [unitOfCompetencyId],
      },
      { enabled: isMarkWpOpen },
    );

  const wouldCompleteCourse = wouldCompleteCourseData?.wouldCompleteCourse ?? false;

  return (
    <>
      <Stack>
        <Menu>
          <MenuButton
            as={IconButton}
            aria-label="Options"
            icon={<Ellipsis size={20} />}
            variant="ghost"
            size="sm"
            onClick={(e) => e.stopPropagation()}
          />
          <MenuList p={0}>
            {!isUnitExempted ? (
              <MenuItem
                icon={<CircleOff size={16} />}
                aria-label="Mark as not required"
                onClick={(e) => {
                  e.stopPropagation();
                  onOpen();
                }}
                isDisabled={enrolmentStatus === StudentCourseEnrolmentStatus.PAUSED}
              >
                <Trans>Mark as not required</Trans>
              </MenuItem>
            ) : null}
            {Boolean(previousItemDueDate) &&
            unitScheduleOverallStatus !== RESOURCE_STATUSES.COMPLETED ? (
              <MenuItem
                icon={<CalendarSync size={16} />}
                aria-label="Update schedule"
                onClick={(e) => {
                  e.stopPropagation();
                  onUpdateScheduleOpen();
                }}
                isDisabled={enrolmentStatus === StudentCourseEnrolmentStatus.PAUSED}
              >
                <Trans>Update schedule</Trans>
              </MenuItem>
            ) : null}
            {isWorkPlacement && !isWorkPlacementComplete && canAssessWorkPlacements ? (
              <MenuItem
                icon={<ClipboardCheck size={16} />}
                aria-label="Mark work placement complete"
                onClick={(e) => {
                  e.stopPropagation();
                  onMarkWpOpen();
                }}
                isDisabled={enrolmentStatus === StudentCourseEnrolmentStatus.PAUSED}
              >
                <Trans>Mark work placement complete for this unit</Trans>
              </MenuItem>
            ) : null}
            <MenuItem
              icon={<History size={16} />}
              aria-label="Unit history"
              onClick={(e) => {
                e.stopPropagation();
                onUnitHistoryOpen();
              }}
            >
              <Trans>Unit history</Trans>
            </MenuItem>
          </MenuList>
        </Menu>
      </Stack>

      {/* Mark as not required  */}
      <Modal size="3xl" isOpen={isOpen} onClose={handleClose}>
        <ModalOverlay />
        <ModalContent
          padding={4}
          h={'500px'}
          as={'form'}
          onSubmit={handleSubmit(onMarkUnitAsNotRequired)}
        >
          <ModalHeader>
            <Trans>Unit not required</Trans>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6} h="full">
            <VStack>
              <FormControl id="category" isInvalid={!!errors.category}>
                <FormLabel>
                  <Trans>Type</Trans>
                </FormLabel>
                <Controller
                  name="category"
                  rules={{
                    required: t`Select a type`,
                  }}
                  control={control}
                  render={({ field }) => (
                    <Select
                      name="category"
                      placeholder={t`Please select`}
                      options={noteTypeOptions}
                      onChange={(val: { value: string; label: string } | null) => {
                        field.onChange(val?.value);
                        onFormChange();
                      }}
                    />
                  )}
                />
                <FormErrorMessage>{errors.category?.message}</FormErrorMessage>
              </FormControl>
            </VStack>
            <VStack>
              <FormControl isInvalid={errors.text?.message != null && errors.text.message !== ''}>
                <FormLabel color="fg.emphasis" fontWeight="normal" fontSize="small" mb={1}>
                  <Trans>Add details</Trans>
                </FormLabel>
                <Controller
                  name="text"
                  rules={{
                    required: t`Details are required`,
                  }}
                  control={control}
                  render={({ field }) => <Textarea height="auto" rows={5} mb={2} {...field} />}
                />
                <FormErrorMessage>{errors.text?.message}</FormErrorMessage>
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button
              type="submit"
              isLoading={isMarkingNotRequired}
              isDisabled={isMarkingNotRequired}
            >
              <Trans>Mark as not required</Trans>
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Update schedule */}
      <Modal size="3xl" isOpen={isUpdateScheduleOpen} onClose={onUpdateScheduleClose}>
        <ModalOverlay />
        <ModalContent padding={4} h={'500px'} as={'form'} onSubmit={handleSubmit(onScheduleUpdate)}>
          <ModalHeader>
            <Trans>Unit schedule</Trans>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6} h="full">
            <VStack>
              <FormControl>
                <FormLabel htmlFor="start_date">
                  <Trans>Start date</Trans>
                </FormLabel>
                <Input
                  type="date"
                  id="schedule"
                  defaultValue={djs(schedule).format('YYYY-MM-DD')}
                  onChange={(event) => {
                    const selectedDate = event.target.value;
                    setSchedule(selectedDate ? new Date(selectedDate) : null);
                  }}
                  min={djs(previousItemDueDate).format('YYYY-MM-DD')}
                />
              </FormControl>
            </VStack>
            <VStack>
              <FormControl isInvalid={errors.text?.message != null && errors.text.message !== ''}>
                <FormLabel color="fg.emphasis" fontWeight="normal" fontSize="small" mb={1}>
                  <Trans>Add note</Trans>
                </FormLabel>
                <Controller
                  name="text"
                  rules={{
                    required: t`Note is required`,
                  }}
                  control={control}
                  render={({ field }) => <Textarea height="auto" rows={5} mb={2} {...field} />}
                />
                <FormErrorMessage>{errors.text?.message}</FormErrorMessage>
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button type="submit" isLoading={isUpdatingSchedule} isDisabled={isUpdatingSchedule}>
              <Trans>Update schedule</Trans>
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Mark work placement complete */}
      <DialogBox
        size="xl"
        isOpen={isMarkWpOpen}
        onClose={onMarkWpClose}
        showCloseButton
        header={<Trans>Mark work placement complete</Trans>}
        body={
          <VStack>
            {wouldCompleteCourse ? <SemesterCompletionAlert /> : null}
            <FormControl>
              <FormLabel color="fg.emphasis" fontWeight="normal" fontSize="sm" mb={1}>
                <Trans>Notes (optional)</Trans>
              </FormLabel>
              <Textarea
                height="auto"
                rows={5}
                mb={2}
                value={wpNotes}
                onChange={(e) => setWpNotes(e.target.value)}
              />
            </FormControl>
          </VStack>
        }
        footer={
          <>
            <Button variant="ghost" mr={3} onClick={onMarkWpClose}>
              <Trans>Cancel</Trans>
            </Button>
            <Button
              isLoading={markWpComplete.isPending}
              isDisabled={markWpComplete.isPending}
              onClick={() =>
                void markWpComplete.mutateAsync({
                  courseEnrolmentId: enrolmentId,
                  unitOfCompetencyIds: [unitOfCompetencyId],
                  notes: wpNotes || undefined,
                })
              }
            >
              <Trans>Mark as complete</Trans>
            </Button>
          </>
        }
      />

      {/* Unit history timeline */}
      <Modal size="4xl" isOpen={isUnitHistoryOpen} onClose={onUnitHistoryClose}>
        <ModalOverlay />
        <ModalContent padding={4} maxH={'80vh'} display="flex" flexDirection="column">
          <ModalHeader>
            <Trans>Unit History Timeline</Trans>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6} flex={1} display="flex" overflow="hidden" minHeight={0}>
            <UnitHistoryTimeline
              data={data}
              unitScheduleOverallStatus={unitScheduleOverallStatus}
            />
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
}
